import type { ComponentChildren } from "preact"
import { useEffect, useRef } from "preact/hooks"
import { IconX } from "./Icons"

interface ModalProps {
	open: boolean
	onClose: () => void
	title?: ComponentChildren
	children?: ComponentChildren
}

// Minimal modal around the native <dialog> element: the browser supplies
// Esc-to-close, the ::backdrop and focus trapping; styles.css supplies the
// look (dialog, .modal-header, .modal-body).
export function Modal({ open, onClose, title, children }: ModalProps) {
	const dialogRef = useRef<HTMLDialogElement>(null)
	const bodyRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const dialog = dialogRef.current
		if (!dialog) return
		if (open) {
			if (!dialog.open) {
				dialog.showModal()
				// showModal() focuses the first focusable descendant on its own,
				// which is the × in the header — so opening a modal put the ring on
				// the way *out* of it. Give focus to whatever the caller marked
				// autofocus (its primary action), and otherwise to the body, which
				// is focusable only programmatically and so shows no ring.
				const target =
					dialog.querySelector<HTMLElement>("[autofocus]") ?? bodyRef.current
				target?.focus()
			}
		} else {
			if (dialog.open) dialog.close()
		}
	}, [open])

	if (!open) return null

	return (
		// biome-ignore lint/a11y/useKeyWithClickEvents: <dialog> handles Esc via onClose; the click closes on the backdrop only.
		<dialog
			ref={dialogRef}
			onClose={onClose}
			onClick={(e) => {
				if (e.target === dialogRef.current) dialogRef.current?.close()
			}}
		>
			<div class="modal-header">
				<h2>{title}</h2>
				<button
					type="button"
					class="link muted icon"
					onClick={onClose}
					aria-label="Close"
				>
					<IconX size="1.25rem" />
				</button>
			</div>
			<div class="modal-body" ref={bodyRef} tabIndex={-1}>
				{children}
			</div>
		</dialog>
	)
}
